import {useTranslation} from "react-i18next";
import {serviceCards} from "../../constants/data";
import {Icons} from "../../constants/svgIcons";
import {Link} from "react-router-dom";

const ServicePage = ({ serviceKey='web' }) => {
  const { t, i18n } = useTranslation();
  const service = serviceCards.find(service => service.key === serviceKey) || serviceCards[0];
  const lang = i18n.language;

  const images = {
    web: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&q=80',
    mobile: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&q=80',
    desktop: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=1200&q=80',
    custom: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80',
  };

  const extraContent = {
    web: {
      sections: [
        { title: lang === 'ar' ? 'تطبيقات ويب قابلة للتوسع' : 'Scalable Web Applications',
          body: lang === 'ar' ? 'نبني تطبيقات ويب تنمو مع أعمالك. من المشاريع الناشئة إلى الحلول المؤسسية، نضمن الأداء والموثوقية.' : 'We build web applications that scale with your business. From startup projects to enterprise solutions, we ensure performance and reliability.',
          img: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80' },
        { title: lang === 'ar' ? 'تجربة مستخدم استثنائية' : 'Exceptional User Experience',
          body: lang === 'ar' ? 'تصاميم جذابة وسهلة الاستخدام تبهج مستخدميك وتحول الزوار إلى عملاء.' : 'Beautiful, intuitive designs that delight your users and convert visitors into customers.',
          img: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=800&q=80' },
      ]
    },
    mobile: {
      sections: [
        {
          title: lang === 'ar' ? 'تطبيقات iOS وAndroid' : 'iOS & Android Apps',
          body: lang === 'ar' ? 'نطور تطبيقات جوال أصلية وعبر منصات للوصول إلى أكبر شريحة ممكنة من المستخدمين.' : 'We develop native and cross-platform mobile apps to reach the widest possible audience.',
          img: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=800&q=80'
        },
        {
          title: lang === 'ar' ? 'تكاملات قوية' : 'Powerful Integrations',
          body: lang === 'ar' ? 'تكامل سلس مع بوابات الدفع وأنظمة إدارة المحتوى وAPIs الخارجية وخدمات السحابة.' : 'Seamless integration with payment gateways, CMS, third-party APIs, and cloud services.',
          img: 'https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?w=800&q=80'
        },
      ]
    },
    desktop: {
      sections: [
        {
          title: lang === 'ar' ? 'أداء عالٍ بدون اتصال' : 'High-Performance Offline',
          body: lang === 'ar' ? 'تطبيقات سطح مكتب قوية تعمل بكفاءة سواء كانت متصلة بالإنترنت أم لا، لسير عمل لا ينقطع.' : 'Powerful desktop apps that work efficiently online and offline, ensuring uninterrupted workflows.',
          img: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80'
        },
        {
          title: lang === 'ar' ? 'تكامل النظام' : 'System Integration',
          body: lang === 'ar' ? 'تكامل عميق مع نظام التشغيل، أجهزة الطرفية، ومكونات الشركة الداخلية.' : 'Deep integration with OS, peripheral hardware, and internal company systems.',
          img: 'https://images.unsplash.com/photo-1629654297299-c8506221ca97?w=800&q=80'
        },
      ]
    },
    custom: {
      sections: [
        {
          title: lang === 'ar' ? 'مصمم خصيصاً لك' : 'Built Just for You',
          body: lang === 'ar' ? 'نفهم عمليات عملك بعمق ونبني برمجيات تتلاءم بدقة مع متطلباتك الفريدة.' : 'We deeply understand your business processes and build software that precisely fits your unique requirements.',
          img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80'
        },
        {
          title: lang === 'ar' ? 'أتمتة الأعمال' : 'Business Automation',
          body: lang === 'ar' ? 'اتركنا نتولى المهام الروتينية حتى يتفرغ فريقك للعمل الاستراتيجي الذي يصنع الفارق.' : 'Let us handle the repetitive tasks so your team can focus on strategic work that makes a difference.',
          img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'
        },
      ]
    },
  };

  const sections = extraContent[serviceKey]?.sections || [];

  return (
    <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img src={images[serviceKey]} alt={service.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-600/50 via-theme-color/60 to-gray-800/90" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
            <Link to={'/'} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors">
              ← {lang === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
            </Link>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">{t(`homePage.services.${service.title}`)}</h1>
            <p className="text-white/80 text-xl max-w-2xl">{t(`homePage.services.${service.desc}`)}</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="w-mobile-container md:w-theme-container mx-auto ">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                {lang === 'ar' ? 'ما نقدمه' : 'What We Offer'}
              </h2>
              <div className="space-y-4">
                {service.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-theme-color rounded-xl flex items-center justify-center text-white flex-shrink-0">
                      <Icons.Check />
                    </div>
                    <span className="font-semibold text-gray-800">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-theme-color rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-black mb-6">{lang === 'ar' ? 'هل أنت مستعد للبدء؟' : 'Ready to Get Started?'}</h3>
              <p className="text-white/80 mb-8 leading-relaxed">
                {lang === 'ar' ? 'دعنا نناقش مشروعك ونبتكر حلاً مثالياً يلبي احتياجات عملك.' : "Let's discuss your project and create the perfect solution for your business needs."}
              </p>
              <Link to={'/contact'} className="inline-block bg-white text-[#000db5] px-8 py-4 rounded-full font-black hover:shadow-xl hover:scale-105 transition-all">
                {lang === 'ar' ? 'احصل على عرض سعر مجاني' : 'Get a Free Quote'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Deep sections */}
      {sections.map((sec, i) => (
        <section key={i} className={`py-20 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="w-mobile-container md:w-theme-container mx-auto ">
            <div className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                <h2 className="text-3xl font-black text-gray-900 mb-4">{sec.title}</h2>
                <p className="text-gray-600 text-lg leading-relaxed">{sec.body}</p>
              </div>
              <div className={`rounded-3xl overflow-hidden shadow-xl ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <img src={sec.img} alt={sec.title} className="w-full h-64 md:h-80 object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 bg-theme-color">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-4xl font-black text-white mb-4">{t('common.readyToBuild')}</h2>
          <p className="text-white/80 mb-8">{t('common.letDiscuss')}</p>
          <Link to={'/contact'} className="inline-block bg-white text-[#000db5] px-10 py-4 rounded-full text-lg font-black hover:shadow-xl hover:scale-105 transition-all">
            {t('common.startProject')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ServicePage;
